/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Authenticated API Client
 *
 * Wrapper around the base API client that automatically acquires
 * MSAL access tokens before making requests.
 *
 * Usage in components:
 * ```tsx
 * import { createAuthenticatedClient } from '@/api/authenticatedClient'
 *
 * const MyComponent = () => {
 *   const apiClient = createAuthenticatedClient()
 *
 *   const fetchData = async () => {
 *     const data = await apiClient.get('/submissions')
 *   }
 * }
 * ```
 */

import { setCsrfCookie } from "@/api/csrfCookie";
import { msalInstance, apiScopes } from "@/lib/msalInstance";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import type { ApiResponse, PaginatedData } from "@/lib/types";
import { logger, LogCategory } from "@/lib/logger";
import { getCorrelationId } from "@/lib/correlationId";

// ============================================================================
// Error Types
// ============================================================================

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ============================================================================
// Configuration
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";
const REQUEST_TIMEOUT = 30000;
const MUTATION_TIMEOUT = 60000;

/**
 * Get CSRF token from cookie or meta tag
 */
function getCsrfToken(): string | null {
  // Try cookie first
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrf_token") {
      return decodeURIComponent(value);
    }
  }

  // Try meta tag as fallback
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute("content");
  }

  return null;
}

// ============================================================================
// Types
// ============================================================================

type ParamValue = string | number | boolean | undefined | null;

export interface AuthenticatedRequestOptions extends Omit<RequestInit, "body"> {
  params?: Record<string, ParamValue | ParamValue[]>;
  body?: unknown;
  timeout?: number;
  scopes?: string[];
}

// ============================================================================
// Token Acquisition
// ============================================================================

/**
 * Acquire an access token from MSAL for backend authentication
 *
 * IMPORTANT: With proper API exposure in Entra ID, use access token (not ID token).
 * The access token has the correct audience (api://{client-id}) when using
 * the api://{client-id}/access_as_user scope.
 */
async function acquireAccessToken(scopes?: string[]): Promise<string | null> {
  logger.info(LogCategory.TOKEN, "Attempting to acquire access token for backend authentication");

  try {
    const account = msalInstance.getActiveAccount();

    if (!account) {
      logger.warn(LogCategory.TOKEN, "No active MSAL account found - user needs to login");
      return null;
    }

    // account.username is the UPN/email (PII). logger.info is relayed to the
    // backend log pipeline in prod, so gate this identity log to DEV only.
    if (import.meta.env.DEV) {
      logger.info(LogCategory.TOKEN, "Active account found", {
        username: account.username,
        accountId: account.localAccountId,
        environment: account.environment,
      });
    }

    // IMPORTANT: Use API scopes to get a proper access token for our backend API
    // The exposed API scope (api://{client-id}/access_as_user) must be configured in Entra ID
    const tokenScopes = scopes || apiScopes.scopes;

    // Try silent token acquisition first
    try {
      logger.info(LogCategory.TOKEN, "Attempting silent token acquisition", {
        scopes: tokenScopes,
      });
      const response = await msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account,
      });

      logger.info(LogCategory.TOKEN, "Silent token acquisition successful", {
        scopes: response.scopes,
        tokenType: response.tokenType,
        expiresOn: response.expiresOn,
        hasIdToken: !!response.idToken,
        hasAccessToken: !!response.accessToken,
        idTokenLength: response.idToken.length,
        accessTokenLength: response.accessToken.length,
      });

      // Log access token details for debugging
      if (response.accessToken) {
        try {
          const tokenParts = response.accessToken.split(".");
          if (tokenParts.length === 3) {
            /* v8 ignore next -- tokenParts[1] is always a defined (possibly empty) string here: String#split never produces a sparse array, so `?? ""` cannot observably trigger when length === 3 */
            const payload = JSON.parse(atob(tokenParts[1] ?? "")) as {
              aud?: string;
              iss?: string;
              email?: string;
              preferred_username?: string;
              upn?: string;
              name?: string;
              oid?: string;
              scp?: string;
            };
            // email + name are PII and logger.info is relayed to the backend log
            // pipeline in prod; gate this claims dump to DEV only. (hasEmail, a
            // boolean, is the only identity signal safe to log in prod — see below.)
            if (import.meta.env.DEV) {
              logger.info(LogCategory.TOKEN, "Access Token claims", {
                aud: payload.aud,
                iss: payload.iss,
                email: payload.email ?? payload.preferred_username ?? payload.upn,
                name: payload.name,
                oid: payload.oid,
                scp: payload.scp, // scopes
                hasEmail: !!(payload.email ?? payload.preferred_username ?? payload.upn),
              });
            }
          }
        } catch (e) {
          logger.warn(LogCategory.TOKEN, "Could not decode access token", { error: e });
        }
      } else {
        logger.error(LogCategory.TOKEN, "WARNING: No access token in response!");
      }

      // Return access token for API authorization
      // Access token has correct audience (api://{client-id}) when using API scopes
      logger.info(LogCategory.TOKEN, "Using access token for backend authentication");
      return response.accessToken;
    } catch (silentError: unknown) {
      const isMsalError = (
        err: unknown
      ): err is { errorCode?: string; errorMessage?: string; subError?: string; name?: string } => {
        return typeof err === "object" && err !== null;
      };

      const errorDetails = isMsalError(silentError)
        ? {
            errorCode: silentError.errorCode,
            errorMessage: silentError.errorMessage,
            subError: silentError.subError,
            name: silentError.name,
          }
        : {
            /* v8 ignore next -- unreachable: isMsalError(silentError) already false here (not an object or null), and instanceof Error implies typeof "object", so this consequent can never be taken */
            errorMessage: silentError instanceof Error ? silentError.message : "Unknown error",
            /* v8 ignore next -- unreachable: see errorMessage above; same guard makes `silentError instanceof Error` always false in this branch */
            name: silentError instanceof Error ? silentError.name : "UnknownError",
          };

      logger.error(LogCategory.TOKEN, "Silent token acquisition failed", errorDetails);

      const isBlockIframeError =
        isMsalError(silentError) &&
        (silentError.errorCode === "block_iframe_reload" ||
          silentError.errorCode === "block_iframe" ||
          silentError.name === "BrowserAuthError");

      if (isBlockIframeError) {
        logger.warn(
          LogCategory.TOKEN,
          "Browser blocked iframe-based token refresh, trying popup",
          errorDetails
        );

        try {
          const popupResponse = await msalInstance.acquireTokenPopup({
            scopes: tokenScopes,
            account,
          });

          logger.info(LogCategory.TOKEN, "Popup token acquisition successful", {
            scopes: popupResponse.scopes,
            expiresOn: popupResponse.expiresOn,
          });

          return popupResponse.accessToken;
        } catch (popupError) {
          logger.error(LogCategory.TOKEN, "Popup token acquisition also failed, using redirect", {
            error: popupError instanceof Error ? popupError.message : String(popupError),
          });
          sessionStorage.setItem("torqe-auth-return-url", window.location.href);
          await msalInstance.acquireTokenRedirect({
            scopes: tokenScopes,
            account,
          });
          return null;
        }
      }

      if (silentError instanceof InteractionRequiredAuthError) {
        logger.info(LogCategory.TOKEN, "Interaction required - redirecting to login");

        sessionStorage.setItem("torqe-auth-return-url", window.location.href);

        // Use redirect instead of popup (COOP-compliant)
        await msalInstance.acquireTokenRedirect({
          scopes: tokenScopes,
          account,
        });

        // Note: acquireTokenRedirect never returns - page will reload
        return null;
      }

      // For other errors, try popup as fallback before giving up
      logger.warn(
        LogCategory.TOKEN,
        "Unexpected silent token error, trying popup fallback",
        errorDetails
      );
      try {
        const popupResponse = await msalInstance.acquireTokenPopup({
          scopes: tokenScopes,
          account,
        });
        logger.info(LogCategory.TOKEN, "Popup fallback successful");
        return popupResponse.accessToken;
      } catch (popupError) {
        logger.error(LogCategory.TOKEN, "Popup fallback also failed", {
          error: popupError instanceof Error ? popupError.message : String(popupError),
        });
        throw silentError;
      }
    }
  } catch (error: unknown) {
    // Type guard for error objects
    const isErrorObject = (
      err: unknown
    ): err is {
      errorCode?: string;
      errorMessage?: string;
      name?: string;
      message?: string;
      stack?: string;
    } => {
      return typeof err === "object" && err !== null;
    };

    const errorDetails = isErrorObject(error)
      ? {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
          name: error.name,
          message: error.message,
          stack: error.stack,
        }
      : {
          /* v8 ignore next -- unreachable: isErrorObject(error) already false here (not an object or null), and instanceof Error implies typeof "object", so this consequent can never be taken */
          message: error instanceof Error ? error.message : "Unknown error",
          /* v8 ignore next -- unreachable: see message above; same guard makes `error instanceof Error` always false in this branch */
          name: error instanceof Error ? error.name : "UnknownError",
        };

    logger.error(LogCategory.TOKEN, "Token acquisition error", errorDetails);
    return null;
  }
}

/**
 * Check if demo mode is enabled
 */
function isDemoMode(): boolean {
  try {
    const authState = sessionStorage.getItem("torqe-auth");
    if (authState) {
      const parsed = JSON.parse(authState) as { state?: { isDemoMode?: boolean } };
      return parsed.state?.isDemoMode === true;
    }
  } catch {
    // Ignore parse errors
  }
  return false;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build URL with query parameters
 */
export function buildUrl(path: string, params?: Record<string, ParamValue | ParamValue[]>): string {
  const basePath = API_BASE.replace(/\/$/, "");
  const cleanPath = path.replace(/^\//, "");
  const fullUrl = `${basePath}/${cleanPath}`;

  const url = new URL(fullUrl, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== "") {
            url.searchParams.append(key, String(v));
          }
        });
      } else if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Create fetch with timeout
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(
      new DOMException(
        `Request timed out after ${String(Math.round(timeout / 1000))}s. The operation may have completed — please verify before retrying.`,
        "TimeoutError"
      )
    );
  }, timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================================
// Request Function
// ============================================================================

/**
 * Make an authenticated HTTP request
 */
async function authenticatedRequest<T>(
  method: string,
  path: string,
  options: AuthenticatedRequestOptions = {}
): Promise<T> {
  const { params, body, timeout = REQUEST_TIMEOUT, scopes, ...fetchOptions } = options;

  const url = buildUrl(path, params);

  logger.debug(LogCategory.API, "Making authenticated request", {
    method,
    path,
    url,
    hasBody: !!body,
    scopes,
  });

  // Acquire access token (or use demo token)
  let token: string | null;

  if (isDemoMode()) {
    logger.info(LogCategory.API, "Using demo mode token");
    token = "demo-token";
  } else {
    logger.info(LogCategory.API, "Acquiring MSAL access token...");
    token = await acquireAccessToken(scopes);

    if (!token) {
      logger.error(LogCategory.API, "Failed to acquire token - throwing 401");
      throw new ApiError("Failed to acquire access token. Please log in again.", 401);
    }

    // Log token metadata only in development (no PII in production)
    if (import.meta.env.DEV) {
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length === 3) {
          /* v8 ignore next -- tokenParts[1] is always a defined (possibly empty) string here: String#split never produces a sparse array, so `?? ""` cannot observably trigger when length === 3 */
          const payload = JSON.parse(atob(tokenParts[1] ?? "")) as { exp?: number; ver?: string };
          logger.debug(LogCategory.API, "Token acquired", {
            hasAccessToken: true,
            exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : undefined,
            tokenType: payload.ver ? `v${payload.ver}` : "unknown",
          });
        }
      } catch {
        // Token decode for logging is best-effort
      }
    }

    logger.info(LogCategory.API, "Access token acquired successfully", {
      tokenLength: token.length,
    });
  }

  const headers = new Headers(fetchOptions.headers);
  headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  headers.set("Authorization", `Bearer ${token}`);

  // Correlation ID: a stable, session-scoped id (shared with the logger via
  // getCorrelationId) so browser-originated activity threads together with the
  // backend spans/logs in Grafana (LGTM integration doc §8). Must NOT be a fresh
  // per-request UUID — that would make every request/log line uncorrelatable.
  // Full W3C traceparent / browser spans are deferred.
  if (!headers.has("x-correlation-id")) {
    headers.set("x-correlation-id", getCorrelationId());
  }

  // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
  const unsafeMethods = ["POST", "PUT", "PATCH", "DELETE"];
  if (unsafeMethods.includes(method.toUpperCase())) {
    // Try to get CSRF token from cookie or meta tag
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers.set("X-CSRF-Token", csrfToken);
    }
  }

  logger.debug(LogCategory.API, "Sending request with headers");

  // Spread caller-supplied fetchOptions FIRST so our computed method/headers/body/
  // credentials win. Previously fetchOptions was spread last, so a caller passing
  // `headers` clobbered the Authorization / x-correlation-id / CSRF / Content-Type
  // headers built above — producing 401s on any request with custom options.
  const response = await fetchWithTimeout(
    url,
    {
      ...fetchOptions,
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
    },
    timeout
  );

  // Extract CSRF token from response header and store it
  const csrfTokenFromHeader = response.headers.get("X-CSRF-Token");
  if (csrfTokenFromHeader) {
    // Store CSRF token in cookie for future requests (Secure on HTTPS — TRQ-04).
    setCsrfCookie(csrfTokenFromHeader);
  }

  logger.debug(LogCategory.API, "Response received", {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
  });

  // Handle non-2xx responses
  if (!response.ok) {
    let errorData: {
      message?: string;
      error?: { code?: string; details?: Record<string, unknown> };
    } = {};

    try {
      errorData = (await response.json()) as typeof errorData;
    } catch {
      // Response body is not JSON
    }

    logger.error(LogCategory.API, "Request failed", {
      status: response.status,
      statusText: response.statusText,
      errorData,
    });

    throw new ApiError(
      errorData.message ?? `HTTP ${String(response.status)}: ${response.statusText}`,
      response.status,
      errorData.error?.code,
      errorData.error?.details
    );
  }

  // Handle empty responses
  if (response.status === 204) {
    logger.debug(LogCategory.API, "Empty response (204)");
    return undefined as T;
  }

  logger.debug(LogCategory.API, "Request successful");
  return response.json() as Promise<T>;
}

// ============================================================================
// HTTP Methods
// ============================================================================

export const authenticatedApi = {
  get: <T>(path: string, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>("GET", path, options),

  post: <T>(path: string, body?: unknown, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>("POST", path, { timeout: MUTATION_TIMEOUT, ...options, body }),

  put: <T>(path: string, body?: unknown, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>("PUT", path, { timeout: MUTATION_TIMEOUT, ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>("PATCH", path, { timeout: MUTATION_TIMEOUT, ...options, body }),

  delete: <T>(path: string, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>("DELETE", path, { timeout: MUTATION_TIMEOUT, ...options }),
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Unwrap API response to get data or throw error
 */
export function unwrapResponse<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === null) {
    throw new ApiError(
      response.message || "Request failed",
      500,
      response.error?.code,
      response.error?.details
    );
  }
  return response.data;
}

/**
 * Unwrap paginated API response
 */
export function unwrapPaginatedResponse<T>(
  response: ApiResponse<PaginatedData<T>>
): PaginatedData<T> {
  return unwrapResponse(response);
}

// ============================================================================
// File Upload
// ============================================================================

/**
 * Upload a file with progress tracking using MSAL authentication
 */
export async function uploadFile(
  path: string,
  file: File,
  options?: {
    onProgress?: (progress: number) => void;
    additionalData?: Record<string, string>;
  }
): Promise<ApiResponse<{ fileId: string; blobUri: string }>> {
  const formData = new FormData();
  formData.append("file", file);

  if (options?.additionalData) {
    Object.entries(options.additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const url = buildUrl(path, undefined);

  // Acquire token using MSAL
  let token: string | null = null;
  if (isDemoMode()) {
    token = "demo-token";
  } else {
    token = await acquireAccessToken();
  }

  if (!token) {
    throw new ApiError("Failed to acquire access token for upload", 401);
  }

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && options?.onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100);
        options.onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      try {
        const response = JSON.parse(xhr.responseText) as ApiResponse<{
          fileId: string;
          blobUri: string;
        }>;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          const errData = response as {
            message?: string;
            error?: { code?: string; details?: Record<string, unknown> };
          };
          reject(
            new ApiError(
              errData.message ?? "Upload failed",
              xhr.status,
              errData.error?.code,
              errData.error?.details
            )
          );
        }
      } catch {
        reject(new ApiError("Failed to parse response", xhr.status));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new ApiError("Network error", 0));
    });

    xhr.addEventListener("abort", () => {
      reject(new ApiError("Upload cancelled", 0));
    });

    xhr.open("POST", url);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || getCsrfToken();
    if (csrfToken) {
      xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    }
    xhr.withCredentials = true;
    xhr.send(formData);
  });
}

/**
 * Download an authenticated file (e.g. a `text/csv` report) as a browser download.
 *
 * The standard `authenticatedApi.get` parses the body as JSON, so a streaming file endpoint needs
 * this raw path: acquire the bearer token, GET with `credentials: "include"`, then save the blob.
 * GET-only, so no CSRF token is attached (matches the server, which exposes reports as GET).
 */
export async function downloadCsv(
  path: string,
  filename: string,
  params?: Record<string, ParamValue | ParamValue[]>
): Promise<void> {
  // Mirror authenticatedRequest's token acquisition exactly: demo mode uses the demo token (no
  // MSAL), otherwise acquire an MSAL token and fail before the network call if none is available
  // (so a session-expired user is handled by the app's auth flow, not an opaque "Download failed").
  let token: string | null;
  if (isDemoMode()) {
    token = "demo-token";
  } else {
    token = await acquireAccessToken();
    if (!token) {
      throw new ApiError("Failed to acquire access token. Please log in again.", 401);
    }
  }

  const headers = new Headers();
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("x-correlation-id", getCorrelationId());

  const response = await fetch(buildUrl(path, params), {
    method: "GET",
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    throw new ApiError(`Download failed (${String(response.status)})`, response.status);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Defer revocation: revoking synchronously (or too soon) can abort the download before the
  // browser's download manager has read the blob — an empty/truncated file on Firefox or on
  // Chromium under memory pressure with large CSVs. 1000ms is the widely-used safe minimum for
  // this pattern (well above the sub-frame window the download manager needs to claim the blob).
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

// ============================================================================
// Export
// ============================================================================

export default authenticatedApi;
